import { fontSize, fontWeight, theme } from "@tm/utility/global";
import styled, { css } from "styled-components";

export const CompareWrapper = styled.div`
  width: 100%;
  background: ${theme.whiteText};
  border-radius: 16px;
  box-shadow: 0 10px 30px ${theme.greyText5}1a;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CompareHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CompareTitle = styled.h2`
  margin: 0;
  font-size: 32px;
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
  line-height: 1.2;
`;

export const CompareSubtitle = styled.p`
  margin: 0;
  font-size: ${fontSize.font16};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText};
  line-height: 1.4;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background: ${theme.disabledGrey2};
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  gap: 4px;
`;

export const FilterChip = styled.button<{ $active: boolean }>`
  padding: 5px 12px 6px;
  border-radius: 8px;
  border: 1px solid ${theme.lightSilver};
  background: ${theme.whiteText};
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  ${({ $active }) =>
    $active &&
    css`
      background: ${theme.lightBlueBg};
      border-color: ${theme.blueText};
      color: ${theme.blueText};
    `}

  &:hover {
    background: ${theme.lightBlueBg};
    border-color: ${theme.blueText};
    color: ${theme.blueText};
  }
`;

export const ContentCard = styled.div`
  position: relative;
  overflow: visible;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const ContentCardInner = styled.div`
  border: 1px solid ${theme.lightSilver};
  border-radius: 16px;
  overflow: hidden;
  isolation: isolate;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const TableScrollArea = styled.div`
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
  min-height: 0;
`;

export const Table = styled.div<{ $planCount: number }>`
  --plan-columns: ${({ $planCount }) => $planCount};
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableRow = styled.div<{ $alternate?: boolean }>`
  display: table-row;
  height: 60px;

  ${({ $alternate }) =>
    $alternate &&
    css`
      background: ${theme.smokeWhite};
    `}
`;

export const TableHeaderRow = styled.div`
  display: table-row;
  height: 60px;
  background: ${theme.whiteText};
`;

export const TableCell = styled.div<{
  $isFirst?: boolean;
  $isRecommended?: boolean;
  $alternate?: boolean;
}>`
  width: calc((100% - 214px) / var(--plan-columns));
  min-width: calc((100% - 214px) / var(--plan-columns));
  display: table-cell;
  height: 60px;
  padding: 0 16px;
  vertical-align: middle;
  text-align: center;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.bold};
  color: ${theme.blackText};
  border-right: 1px solid ${theme.lightSilver};

  &:last-child {
    border-right: none;
  }

  ${({ $isFirst, $alternate }) =>
    $isFirst &&
    css`
      text-align: left;
      width: 214px;
      min-width: 214px;
      padding-left: 24px;
      position: relative;
      background: ${$alternate ? theme.smokeWhite : theme.whiteText};
      z-index: 2;
      box-shadow: 4px 0 20px ${theme.greyText7}30;
      clip-path: inset(0 -20px 0 0);
      border-right: none;
    `}

  ${({ $isRecommended, $alternate }) =>
    $isRecommended &&
    css`
      background: ${$alternate ? "#edf4fb" : theme.blueText2};
    `}
`;

export const HeaderCell = styled(TableCell)`
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font12};
  color: ${theme.blackText};
  text-transform: uppercase;
  padding: 16px;
  vertical-align: middle;
  background: ${theme.whiteText};

  &:first-child {
    z-index: 5;
    text-align: center;
  }

  ${({ $isRecommended }) =>
    $isRecommended &&
    css`
      background: ${theme.blueText2};
    `}
`;

export const PlanHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

export const BadgeTag = styled.span<{
  $gradient?: string[];
  $color?: string;
}>`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  padding: 2px 8px;
  border-radius: 16px;
  font-size: ${fontSize.font12};
  font-weight: ${fontWeight.bold};
  color: ${({ $color }) => $color || theme.whiteText};
  white-space: nowrap;
  text-transform: uppercase;
  background: ${({ $gradient }) =>
    $gradient && $gradient.length >= 2
      ? `linear-gradient(9deg, ${$gradient[0]}, ${$gradient[1]})`
      : theme.blueText};
`;

export const FloatingBadgeTag = styled(BadgeTag)`
  top: -10px;
  pointer-events: none;
`;

export const PlanIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

export const BenefitCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const BenefitIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
`;

export const BenefitLabel = styled.span`
  font-size: ${fontSize.font12};
  font-weight: ${fontWeight.regular};
  color: ${theme.blackText};
  line-height: 1.3;
`;

export const InfoTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  width: 14px;
  height: 14px;
  position: relative;
  margin-left: auto;
`;

export const FixedTooltip = styled.div`
  position: fixed;
  z-index: 10000;
  width: 220px;
  padding: 12px;
  background: ${theme.persianGreen};
  border-radius: 4px;
  color: ${theme.whiteText};
  font-size: ${fontSize.font14};
  line-height: 1.4;

  &::before {
    content: "";
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 8px solid transparent;
    border-right-color: ${theme.persianGreen};
  }
`;

export const CellValueIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

export const DashValue = styled.span`
  opacity: 0.5;
`;

export const FooterRow = styled.div`
  display: table-row;
  background: ${theme.whiteText};
`;

export const FooterCell = styled.div<{
  $isFirst?: boolean;
  $isRecommended?: boolean;
}>`
  width: calc((100% - 214px) / var(--plan-columns));
  min-width: calc((100% - 214px) / var(--plan-columns));
  display: table-cell;
  padding: 20px 16px;
  vertical-align: middle;
  text-align: center;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.bold};
  color: ${theme.blackText};
  border-right: 1px solid ${theme.lightSilver};
  background: ${theme.whiteText};

  &:last-child {
    border-right: none;
  }

  ${({ $isFirst }) =>
    $isFirst &&
    css`
      text-align: left;
      width: 214px;
      min-width: 214px;
      padding-left: 24px;
      border-right: none;
    `}

  ${({ $isRecommended }) =>
    $isRecommended &&
    css`
      background: ${theme.blueText2};
    `}
`;

export const FooterPriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  button {
    box-shadow: none;
  }
`;
